// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Options2Line from "@store/Library/Components/Toolkit/Icons/Options2/Options2Line";
import Button, { ButtonPresentationProps, ButtonProps } from "@store/MobiusOverrides/Button/Button";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@store/MobiusOverrides/Drawer/Drawer";
import { boxStickyStyles } from "@store/Themes/common/mixins/box";
import { countBubbleStyles } from "@store/Themes/common/mixins/countBubble";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { createContext, FC, HTMLAttributes, PropsWithChildren, useCallback, useContext, useState } from "react";
import { css } from "styled-components";

export interface FilterDrawerContextModel {
    scrollElement: HTMLElement | null;
}

export const FilterDrawerContext = createContext<FilterDrawerContextModel>({} as any);
export const useFilterDrawerContext = () => useContext(FilterDrawerContext);

export interface FilterDrawerAndButtonPresentationProps extends ExpandStyles<FilterDrawerAndButtonStyles> {}

export interface FilterDrawerAndButtonProps
    extends FilterDrawerAndButtonPresentationProps,
        HTMLAttributes<HTMLDivElement> {
    as?: keyof JSX.IntrinsicElements;
    applyButtonProps?: ButtonProps;
    clearButtonProps?: ButtonProps;
    drawerProps?: Omit<DrawerProps, "theme">;
    filterButtonLabel?: string;
    filterButtonProps?: ButtonProps;
    filterCount?: number;
}

export interface FilterDrawerAndButtonStyles {
    wrapper?: InjectableCss;
    filterButton?: ButtonPresentationProps;
    filterCount?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerContentWrapper?: InjectableCss;
    drawerActionWrapper?: InjectableCss;
    drawerApplyButton?: ButtonPresentationProps;
    drawerClearButton?: ButtonPresentationProps;
}

export const filterDrawerAndButtonStyles: FilterDrawerAndButtonStyles = {
    filterButton: {
        icon: {
            position: "left",
            src: Options2Line,
        },
        mergeCss: true,
        css: css`
            --btn-bg-color: ${getColor("primary")};
            --btn-text-color: ${getColor("invert")};
            position: relative;

            &:hover {
                --btn-bg-color: ${getColor("secondary")};
                --btn-text-color: ${getColor("text")};
            }

            &:disabled {
                --btn-bg-color: ${getColor("button.disabled")};
                --btn-text-color: ${getColor("button.disabled.contrast")};
            }
        `,
    },
    filterCount: {
        css: css`
            ${countBubbleStyles()}
            top: ${pxToRem(1)};
            left: ${pxToRem(32)};
            border-color: var(--btn-bg-color);
            background-color: var(--btn-text-color);
            color: var(--btn-bg-color);
            transition: inherit;
        `,
    },
    drawer: {
        mergeCss: true,
        cssOverrides: {
            drawerBody: css`
                width: ${pxToRem(500)};
            `,
        },
    },
    drawerContentWrapper: {
        css: css`
            flex-grow: 1;
            overflow-x: hidden; /* This prevents a visual issue with dynamic dropdown overflows */
            padding: ${pxToRem(12, 24)};
        `,
    },
    drawerActionWrapper: {
        css: css`
            ${boxStickyStyles()}
            position: sticky;
            bottom: 0;
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(8, 16)};
            padding: ${pxToRem(24)};
        `,
    },
    drawerApplyButton: {
        sizeVariant: "large",
        mergeCss: true,
        css: css`
            flex-grow: 1;
        `,
    },
    drawerClearButton: {
        variant: "tertiary",
        sizeVariant: "large",
        mergeCss: true,
        css: css`
            flex-grow: 1;
        `,
    },
};

const FilterDrawerAndButton: FC<PropsWithChildren<FilterDrawerAndButtonProps>> = ({
    applyButtonProps,
    children,
    clearButtonProps,
    drawerProps,
    extendedStyles,
    filterButtonLabel: baseFilterButtonLabel,
    filterButtonProps,
    filterCount = 0,
    overridedStyles,
    ...htmlAttributes
}) => {
    const styles = useExpandProps(filterDrawerAndButtonStyles, extendedStyles, overridedStyles);
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
    const scrollElementRef = useCallback(setScrollElement, []);
    const filterButtonLabel = baseFilterButtonLabel || translate("Search and Filter");

    return (
        <StyledWrapper {...styles.wrapper} {...htmlAttributes}>
            <Button
                {...styles.filterButton}
                {...filterButtonProps}
                data-test-selector="filterDrawer_openButton"
                onClick={(...args) => {
                    filterButtonProps?.onClick?.(...args);
                    setDrawerIsOpen(true);
                }}
            >
                {filterButtonLabel}
                {Boolean(filterCount > 0) && (
                    <Typography {...styles.filterCount}>
                        <span aria-hidden="true">{filterCount}</span>
                        <VisuallyHidden>{translate("({0} active filter)", filterCount.toString())}</VisuallyHidden>
                    </Typography>
                )}
            </Button>
            <Drawer
                {...styles.drawer}
                headline={filterButtonLabel}
                isOpen={drawerIsOpen}
                position="right"
                {...drawerProps}
                handleClose={(...args) => {
                    drawerProps?.handleClose?.(...args);
                    setDrawerIsOpen(false);
                }}
            >
                <StyledWrapper {...styles.drawerContentWrapper} ref={scrollElementRef}>
                    <FilterDrawerContext.Provider value={{ scrollElement }}>{children}</FilterDrawerContext.Provider>
                </StyledWrapper>
                <StyledWrapper {...styles.drawerActionWrapper}>
                    <Button
                        {...styles.drawerApplyButton}
                        {...applyButtonProps}
                        data-test-selector="filterDrawer_applyButton"
                        onClick={(...args) => {
                            applyButtonProps?.onClick?.(...args);
                            setDrawerIsOpen(false);
                        }}
                    >
                        {translate("Apply Filters")}
                    </Button>
                    <Button
                        {...styles.drawerClearButton}
                        {...clearButtonProps}
                        data-test-selector="filterDrawer_resetButton"
                    >
                        {translate("Reset Filters")}
                    </Button>
                </StyledWrapper>
            </Drawer>
        </StyledWrapper>
    );
};

export default FilterDrawerAndButton;
